import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Paper, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { SupplierBillService } from '../../services/supplierBill.service';
import {
  SupplierBill, SupplierBillStatus, SUPPLIER_BILL_STATUS_COLORS,
  SupplierBillEdit,
} from '../../interfaces/procurement/supplierBill.interface';
import { ReverseConfirmDialog } from './ReverseConfirmDialog';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { EditHistoryPanel, EditHistoryRow } from './EditHistoryPanel';

const RELEASE_ROLES = ['Accountant', 'InstitutionAdmin'];
const EDIT_ROLES = ['Accountant', 'InstitutionAdmin'];

const STATUS_COLORS = SUPPLIER_BILL_STATUS_COLORS;

const REVERSIBLE_STATUSES: SupplierBillStatus[] = ['UNPAID', 'PARTIALLY_PAID', 'PAID', 'BLOCKED'];
const PAYMENT_ELIGIBLE_STATUSES: SupplierBillStatus[] = ['UNPAID', 'PARTIALLY_PAID'];

export const SupplierBillDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [bill, setBill] = useState<SupplierBill | null>(null);
  const [loading, setLoading] = useState(true);
  const [reverseDialogOpen, setReverseDialogOpen] = useState(false);
  const [reversing, setReversing] = useState(false);
  const [releaseDialogOpen, setReleaseDialogOpen] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const [justification, setJustification] = useState('');
  const [editHistoryRows, setEditHistoryRows] = useState<EditHistoryRow[]>([]);

  useEffect(() => {
    if (!id) return;
    SupplierBillService.getById(id)
      .then(b => {
        setBill(b);
        if ((b.editCount ?? 0) > 0) {
          SupplierBillService.editHistory(id)
            .then((edits: SupplierBillEdit[]) => {
              setEditHistoryRows(edits.map(e => ({
                editSeq: e.editSeq,
                editedAt: e.editedAt,
                editReason: e.editReason,
                summary: `total ${e.prevTotalAmount} → ${b.totalAmount} (was ${e.prevStatus})`,
              })));
            })
            .catch(() => setEditHistoryRows([]));
        }
      })
      .catch(e => toast.showApiError(e, 'Failed to load supplier bill'))
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleReverse = async () => {
    if (!bill) return;
    setReversing(true);
    try {
      const updated = await SupplierBillService.reverse(bill.id);
      setBill(updated);
      toast.showSuccess('Supplier bill reversed');
      setReverseDialogOpen(false);
    } catch (e) {
      toast.showApiError(e, 'Failed to reverse bill');
    } finally {
      setReversing(false);
    }
  };

  const handleRelease = async () => {
    if (!bill) return;
    setReleasing(true);
    try {
      const updated = await SupplierBillService.releaseBlock(bill.id, justification.trim());
      setBill(updated);
      toast.showSuccess('Bill released for payment');
      setReleaseDialogOpen(false);
      setJustification('');
    } catch (e) {
      toast.showApiError(e, 'Failed to release bill');
    } finally {
      setReleasing(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  if (!bill) return <Box sx={{ p: 3 }}><Typography>Supplier bill not found.</Typography></Box>;

  const canReverse = REVERSIBLE_STATUSES.includes(bill.status);
  const canRecordPayment = PAYMENT_ELIGIBLE_STATUSES.includes(bill.status);
  // The JWT `roles` claim is a single string, not an array; normalise so the
  // role checks below (which use Array methods) don't throw. Mirrors the guard
  // in AllocationRuleList.tsx.
  const rawRoles = AuthService.getCurrentUser()?.roles;
  const userRoles: string[] = Array.isArray(rawRoles) ? rawRoles : rawRoles ? [rawRoles] : [];
  const canRelease = bill.status === 'BLOCKED' && userRoles.some(r => RELEASE_ROLES.includes(r));
  const canEdit = (bill.status === 'UNPAID' || bill.status === 'BLOCKED')
    && (bill.paidAmount ?? 0) === 0
    && userRoles.some(r => EDIT_ROLES.includes(r));
  const hasVariance = bill.lines.some(l => l.poUnitPrice != null && l.poUnitPrice !== l.unitPrice);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">{bill.billNumber}</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {canEdit && (
            <Button variant="outlined" onClick={() => navigate(`/procurement/supplier-bills/${bill.id}/edit`)}>
              Edit
            </Button>
          )}
          {canRelease && (
            <Button variant="contained" color="warning" onClick={() => setReleaseDialogOpen(true)}>
              Release block
            </Button>
          )}
          {canReverse && (
            <Button variant="outlined" color="error" onClick={() => setReverseDialogOpen(true)}>
              Reverse
            </Button>
          )}
          {canRecordPayment && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate(`/procurement/supplier-payments/new?supplierBillId=${bill.id}`)}
            >
              Record Payment
            </Button>
          )}
          <Button variant="outlined" onClick={() => navigate(-1)}>Back</Button>
        </Box>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">Bill Number</Typography>
            <Typography>{bill.billNumber}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Status</Typography>
            <Chip label={bill.status} color={STATUS_COLORS[bill.status]} size="small" />
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Purchase Order ID</Typography>
            <Typography>{bill.purchaseOrderId}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Supplier ID</Typography>
            <Typography>{bill.supplierId}</Typography>
          </Box>
          {bill.supplierInvoiceRef && (
            <Box>
              <Typography variant="body2" color="text.secondary">Supplier Invoice Ref</Typography>
              <Typography>{bill.supplierInvoiceRef}</Typography>
            </Box>
          )}
          <Box>
            <Typography variant="body2" color="text.secondary">Bill Date</Typography>
            <Typography>{bill.billDate}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Due Date</Typography>
            <Typography>{bill.dueDate}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Subtotal</Typography>
            <Typography>{bill.subtotal}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Tax Amount</Typography>
            <Typography>{bill.taxAmount}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Total Amount</Typography>
            <Typography>{bill.totalAmount}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Paid Amount</Typography>
            <Typography>{bill.paidAmount}</Typography>
          </Box>
          {bill.status === 'REVERSED' && (
            <>
              <Box>
                <Typography variant="body2" color="text.secondary">Reversed By</Typography>
                <Typography>{bill.reversedById ?? '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Reversed Date</Typography>
                <Typography>{bill.reversedDate ?? '-'}</Typography>
              </Box>
            </>
          )}
        </Box>
      </Paper>

      {(bill.blockReason || hasVariance) && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Variance Summary</Typography>
          {bill.blockReason && (
            <Typography variant="body2" color="error" sx={{ mb: 2 }}>{bill.blockReason}</Typography>
          )}
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Item ID</TableCell>
                <TableCell>PO Price</TableCell>
                <TableCell>Bill Price</TableCell>
                <TableCell>Δ</TableCell>
                <TableCell>Δ%</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bill.lines.map(line => {
                const po = line.poUnitPrice;
                const delta = po != null ? line.unitPrice - po : null;
                const deltaPct = po != null && po !== 0 ? (delta! / po) * 100 : null;
                const off = delta != null && delta !== 0;
                return (
                  <TableRow key={line.id} sx={off ? { bgcolor: 'error.light' } : undefined}>
                    <TableCell>{line.itemId}</TableCell>
                    <TableCell>{po ?? '-'}</TableCell>
                    <TableCell>{line.unitPrice}</TableCell>
                    <TableCell>{delta != null ? delta.toFixed(4) : '-'}</TableCell>
                    <TableCell>{deltaPct != null ? `${deltaPct.toFixed(2)}%` : '-'}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}

      {bill.lines.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Lines</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Line ID</TableCell>
                <TableCell>GRN Line ID</TableCell>
                <TableCell>Item ID</TableCell>
                <TableCell>Quantity</TableCell>
                <TableCell>Unit Price</TableCell>
                <TableCell>Line Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bill.lines.map(line => (
                <TableRow key={line.id}>
                  <TableCell>{line.id}</TableCell>
                  <TableCell>{line.goodsReceiptLineId}</TableCell>
                  <TableCell>{line.itemId}</TableCell>
                  <TableCell>{line.quantity}</TableCell>
                  <TableCell>{line.unitPrice}</TableCell>
                  <TableCell>{line.lineTotal}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {(bill.editCount ?? 0) > 0 && (
        <EditHistoryPanel title="Edit history" rows={editHistoryRows} />
      )}

      <ReverseConfirmDialog
        open={reverseDialogOpen}
        entityLabel="Supplier Bill"
        consequence="AP and Inventory accruals will be reversed. Any payments must be reversed first."
        onCancel={() => setReverseDialogOpen(false)}
        onConfirm={handleReverse}
        loading={reversing}
      />

      <Dialog open={releaseDialogOpen} onClose={() => setReleaseDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Release blocked bill</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This bill is BLOCKED for a price/tax variance. Releasing it makes it payable;
            the posted journal entry is unchanged. A justification is required.
          </DialogContentText>
          <TextField
            label="Justification"
            value={justification}
            onChange={e => setJustification(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReleaseDialogOpen(false)} disabled={releasing}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleRelease}
            disabled={releasing || justification.trim().length === 0}
          >
            {releasing ? <CircularProgress size={20} /> : 'Confirm release'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupplierBillDetail;
